import React, { useEffect, useRef } from "react";
import { loadGoogleMaps } from "../propertydetails/NearbyMap";

/**
 * Stage 2's confirmation map (guided-search wizard + the inline "Talk to
 * Me" chat's own stage-2 hold, see AiMessage.js) -- one pin per geocoded
 * area from personal_agent/router's `areaPins` (StageAnswerResponse) or
 * AiChatResponse.onboardingAreaPins, so the user sees what was actually
 * understood instead of trusting a text extraction blindly. Deliberately
 * minimal: no routes, no nearby places, no clustering -- NearbyMap.jsx
 * already owns that complexity for the property details page, and reusing
 * it wholesale here would be a mismatch for "show 1-3 pins and let the user
 * confirm." Reuses only its map-script loader, not the component itself.
 *
 * `interactive`/`onPinMove` are opt-in (default off, matching the wizard's
 * original display-only behavior exactly) -- only the inline chat's single-
 * area confirmation passes them, to let the user drag the pin or click
 * elsewhere on the map to correct it before confirming. Multi-area answers
 * stay display-only even when `interactive` is passed: there's no single
 * pin a drag/click could unambiguously mean to move.
 */
export default function AreaConfirmMap({ pins, interactive = false, onPinMove }) {
  const containerRef = useRef(null);
  const onPinMoveRef = useRef(onPinMove);
  onPinMoveRef.current = onPinMove;

  const canEdit = interactive && pins?.length === 1;

  useEffect(() => {
    if (!pins?.length || !containerRef.current) return;
    let cancelled = false;

    loadGoogleMaps()
      .then(() => {
        if (cancelled || !containerRef.current) return;
        const G = window.google.maps;
        const map = new G.Map(containerRef.current, {
          zoom: pins.length > 1 ? 12 : 14,
          center: { lat: pins[0].lat, lng: pins[0].lng },
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          disableDefaultUI: true,
        });
        const bounds = new G.LatLngBounds();
        let marker = null;
        pins.forEach((pin) => {
          const position = { lat: pin.lat, lng: pin.lng };
          const m = new G.Marker({ position, map, title: pin.name, draggable: canEdit });
          if (canEdit) marker = m;
          bounds.extend(position);
        });
        if (pins.length > 1) map.fitBounds(bounds, 48);

        if (canEdit && marker) {
          marker.addListener("dragend", () => {
            const pos = marker.getPosition();
            onPinMoveRef.current?.({ lat: pos.lat(), lng: pos.lng() });
          });
          map.addListener("click", (e) => {
            const pos = e.latLng;
            marker.setPosition(pos);
            onPinMoveRef.current?.({ lat: pos.lat(), lng: pos.lng() });
          });
        }
      })
      .catch(() => {
        // Map failed to load (network, missing key, etc.) -- the Confirm
        // button and area names still work without it; this is a visual
        // aid, not a blocking dependency.
      });

    return () => { cancelled = true; };
  }, [pins, canEdit]);

  if (!pins?.length) return null;

  return (
    <div
      ref={containerRef}
      className={`pa-area-map${canEdit ? " pa-area-map-editable" : ""}`}
      role="img"
      aria-label={
        canEdit
          ? `Map showing ${pins[0].name} -- drag the pin or tap the map to adjust`
          : `Map showing ${pins.map((p) => p.name).join(", ")}`
      }
    />
  );
}
